import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";

/**
 * 🌐 PUBLIC SERVICES
 */
import { publicSettingsService } from "../api/service/public/settings.service";
import { publicServicesService } from "../api/service/public/services.service";
import { publicProductsService } from "../api/service/public/products.service";
import { publicProjectsService } from "../api/service/public/projects.service";
import { publicContactService } from "../api/service/public/contact.service";

/**
 * 📦 INTERFACES
 */
import { CompanySettings } from "../api/interface/public.interface";
import { Service } from "../api/interface/services.interface";
import { Product } from "../api/interface/products.interface";
import { Project } from "../api/interface/projects.interface";
import { ProjectCategory } from "../api/interface/projectcategories.interface";
import { ProductCategory } from "../api/interface/productcategories.interface";

// ===============================
// TYPES
// ===============================
interface PublicContextType {
  settings: CompanySettings | null;

  services: Service[] | null;
  projects: Project[] | null;
  products: Product[] | null;

  projectCategories: ProjectCategory[] | null;
  productCategories: ProductCategory[] | null;

  isLoadingSettings: boolean;
  isLoadingServices: boolean;
  isLoadingProjects: boolean;
  isLoadingProducts: boolean;
  isLoadingProjectCategories: boolean;
  isLoadingProductCategories: boolean;

  refreshSettings: () => Promise<void>;
  refreshServices: () => Promise<void>;
  refreshProjects: () => Promise<void>;
  refreshProducts: () => Promise<void>;
  refreshProjectCategories: () => Promise<void>;
  refreshProductCategories: () => Promise<void>;

  sendContactMessage: (payload: any) => Promise<any>;
}

// ===============================
// CONTEXT
// ===============================
const PublicContext = createContext<PublicContextType | undefined>(undefined);

// ===============================
// PROVIDER
// ===============================
export const PublicProvider = ({ children }: { children: ReactNode }) => {
  // ===============================
  // STATE
  // ===============================
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [services, setServices] = useState<Service[] | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [products, setProducts] = useState<Product[] | null>(null);

  const [projectCategories, setProjectCategories] = useState<ProjectCategory[] | null>(null);
  const [productCategories, setProductCategories] = useState<ProductCategory[] | null>(null);

  // ===============================
  // LOADING STATES
  // ===============================
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingProjectCategories, setIsLoadingProjectCategories] = useState(false);
  const [isLoadingProductCategories, setIsLoadingProductCategories] = useState(false);

  // ===============================
  // REFS (ANTI DOUBLE CALL)
  // ===============================
  const fetchingSettingsRef = useRef(false);
  const fetchingServicesRef = useRef(false);
  const fetchingProjectsRef = useRef(false);
  const fetchingProductsRef = useRef(false);
  const fetchingProjectCategoriesRef = useRef(false);
  const fetchingProductCategoriesRef = useRef(false);

  // ===============================
  // SETTINGS
  // ===============================
  const loadSettings = useCallback(async () => {
    if (fetchingSettingsRef.current) return;
    fetchingSettingsRef.current = true;

    try {
      setIsLoadingSettings(true);
      const res = await publicSettingsService.getSettings();
      setSettings(res);
    } catch {
      setSettings(null);
    } finally {
      fetchingSettingsRef.current = false;
      setIsLoadingSettings(false);
    }
  }, []);

  // ===============================
  // SERVICES
  // ===============================
  const loadServices = useCallback(async () => {
    if (fetchingServicesRef.current) return;
    fetchingServicesRef.current = true;

    try {
      setIsLoadingServices(true);
      const res = await publicServicesService.getServices();
      setServices(res.data.services);
    } catch {
      setServices(null);
    } finally {
      fetchingServicesRef.current = false;
      setIsLoadingServices(false);
    }
  }, []);

  // ===============================
  // PROJECTS
  // ===============================
  const loadProjects = useCallback(async () => {
    if (fetchingProjectsRef.current) return;
    fetchingProjectsRef.current = true;

    try {
      setIsLoadingProjects(true);
      const res = await publicProjectsService.getProjects();
      setProjects(res.data.projects);
    } catch {
      setProjects(null);
    } finally {
      fetchingProjectsRef.current = false;
      setIsLoadingProjects(false);
    }
  }, []);

  // ===============================
  // PRODUCTS
  // ===============================
  const loadProducts = useCallback(async () => {
    if (fetchingProductsRef.current) return;
    fetchingProductsRef.current = true;

    try {
      setIsLoadingProducts(true);
      const res = await publicProductsService.getProducts();
      setProducts(res.data.products);
    } catch {
      setProducts(null);
    } finally {
      fetchingProductsRef.current = false;
      setIsLoadingProducts(false);
    }
  }, []);

  // ===============================
  // PROJECT CATEGORIES
  // ===============================
  const loadProjectCategories = useCallback(async () => {
    if (fetchingProjectCategoriesRef.current) return;
    fetchingProjectCategoriesRef.current = true;

    try {
      setIsLoadingProjectCategories(true);
      const res = await publicProjectsService.getProjectCategories();
      setProjectCategories(res.data.categories);
    } catch {
      setProjectCategories(null);
    } finally {
      fetchingProjectCategoriesRef.current = false;
      setIsLoadingProjectCategories(false);
    }
  }, []);

  // ===============================
  // PRODUCT CATEGORIES
  // ===============================
  const loadProductCategories = useCallback(async () => {
    if (fetchingProductCategoriesRef.current) return;
    fetchingProductCategoriesRef.current = true;

    try {
      setIsLoadingProductCategories(true);
      const res = await publicProductsService.getProductCategories();
      setProductCategories(res.data.categories);
    } catch {
      setProductCategories(null);
    } finally {
      fetchingProductCategoriesRef.current = false;
      setIsLoadingProductCategories(false);
    }
  }, []);

  // ===============================
  // CONTACT
  // ===============================
  const sendContactMessage = useCallback(async (payload: any) => {
    return await publicContactService.sendContactMessage(payload);
  }, []);

  // ===============================
  // REFRESH
  // ===============================
  const refreshSettings = useCallback(async () => loadSettings(), [loadSettings]);
  const refreshServices = useCallback(async () => loadServices(), [loadServices]);
  const refreshProjects = useCallback(async () => loadProjects(), [loadProjects]);
  const refreshProducts = useCallback(async () => loadProducts(), [loadProducts]);
  const refreshProjectCategories = useCallback(async () => loadProjectCategories(), [loadProjectCategories]);
  const refreshProductCategories = useCallback(async () => loadProductCategories(), [loadProductCategories]);

  // ===============================
  // INIT
  // ===============================
  useEffect(() => {
    loadSettings();
    loadServices();
    loadProjects();
    loadProducts();
    loadProjectCategories();
    loadProductCategories();
  }, [
    loadSettings,
    loadServices,
    loadProjects,
    loadProducts,
    loadProjectCategories,
    loadProductCategories,
  ]);

  // ===============================
  // VALUE
  // ===============================
  const value = useMemo(
    () => ({
      settings,

      services,
      projects,
      products,

      projectCategories,
      productCategories,

      isLoadingSettings,
      isLoadingServices,
      isLoadingProjects,
      isLoadingProducts,
      isLoadingProjectCategories,
      isLoadingProductCategories,

      refreshSettings,
      refreshServices,
      refreshProjects,
      refreshProducts,
      refreshProjectCategories,
      refreshProductCategories,

      sendContactMessage,
    }),
    [
      settings,
      services,
      projects,
      products,
      projectCategories,
      productCategories,

      isLoadingSettings,
      isLoadingServices,
      isLoadingProjects,
      isLoadingProducts,
      isLoadingProjectCategories,
      isLoadingProductCategories,

      refreshSettings,
      refreshServices,
      refreshProjects,
      refreshProducts,
      refreshProjectCategories,
      refreshProductCategories,

      sendContactMessage,
    ]
  );

  return (
    <PublicContext.Provider value={value}>
      {children}
    </PublicContext.Provider>
  );
};

// ===============================
// HOOK
// ===============================
export const usePublic = () => {
  const context = useContext(PublicContext);

  if (!context) {
    throw new Error("usePublic doit être utilisé dans PublicProvider");
  }

  return context;
};